import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

import { Alert, Button, DocumentUpload, Typography, colors } from '@leapeasy/ui-kit';
import { setDataAction, setErrorAction } from 'store/actions/claimFormActions';
import { createUploadSession, listSessionDocuments } from 'store/sagas/APIs/claimUpload';
import useStagedDocumentUpload from 'hooks/useStagedDocumentUpload';
import { isStagedUploadEnabled, toDisplayFile, documentUploadFileListSx } from 'utils/stagedUpload';
import { getApiErrorMessage } from 'utils/apiError';
import {
  CLAIM_DOCUMENTS_TOO_LARGE_MESSAGE,
  exceedsClaimDocumentsSizeLimit,
  formatClaimDocumentsSizeMb,
  getClaimDocumentsSize,
  MAX_CLAIM_DOCUMENTS_SIZE_MB,
} from 'utils/claimUpload';

const fileTypes = {
  'text/pdf': ['.pdf', '.docx', '.xlsx', '.jpeg', '.png', '.jpg', '.heic'],
};

const documentUploadDescription = `JPEG, PNG, PDF formats, up to 50Mb each (${MAX_CLAIM_DOCUMENTS_SIZE_MB}Mb total)`;

const StagedDocumentUploadField = ({
  category,
  uploadSessionId,
  onError,
  onFilesChange,
  onUploadingChange,
  initialFiles,
  ...documentUploadProps
}) => {
  const { files, handleChange, isUploading, uploadProgress } = useStagedDocumentUpload({
    category,
    uploadSessionId,
    initialFiles,
    onError,
    onFilesChange,
    onUploadingChange,
  });

  return (
    <Box display="flex" flexDirection="column" gap="8px" sx={documentUploadFileListSx}>
      <DocumentUpload
        {...documentUploadProps}
        initialFiles={files}
        onChange={handleChange}
        disabled={isUploading || !uploadSessionId}
      />
      {isUploading && (
        <Box display="flex" flexDirection="column" gap="4px">
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ borderRadius: '4px', height: '6px' }}
          />
          <Typography variant="small" style={{ color: colors.purple[600] }}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const Form5 = (props) => {
  const { actions, formData, handleNext, handleBack } = props;

  const useStaged = isStagedUploadEnabled();
  const [leaseFile, setLeaseFile] = useState([]);
  const [ledgerFile, setLedgerFile] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [acceptableDocument, setAcceptableDocument] = useState('');
  const [uploadSessionId, setUploadSessionId] = useState(formData.data.uploadSessionId || null);
  const [activeUploads, setActiveUploads] = useState(0);
  const shouldReconcileSession = useRef(Boolean(formData.data.uploadSessionId));
  const formDataRef = useRef(formData.data);

  useEffect(() => {
    formDataRef.current = formData.data;
  }, [formData.data]);

  const trackUploading = useCallback((isUploading) => {
    setActiveUploads((count) => Math.max(0, count + (isUploading ? 1 : -1)));
  }, []);

  const handleUploadError = useCallback(
    (message) => {
      actions.setErrorAction(message);
    },
    [actions],
  );

  useEffect(() => {
    setLeaseFile(formData.data.leaseFile || []);
    setLedgerFile(formData.data.ledgerFile || []);
    setOtherFiles(formData.data.otherFiles || []);
    setAcceptableDocument(formData.data.acceptableDocument || '');
    setUploadSessionId(formData.data.uploadSessionId || null);
  }, []);

  useEffect(() => {
    if (!useStaged || uploadSessionId) {
      return undefined;
    }

    let cancelled = false;

    const initSession = async () => {
      try {
        const { uploadSessionId: sessionId } = await createUploadSession();
        if (!cancelled) {
          setUploadSessionId(sessionId);
          actions.setDataAction({
            ...formDataRef.current,
            uploadSessionId: sessionId,
          });
        }
      } catch (error) {
        if (!cancelled) {
          handleUploadError(
            getApiErrorMessage(error, 'Unable to start document upload. Please try again.'),
          );
        }
      }
    };

    initSession();

    return () => {
      cancelled = true;
    };
  }, [useStaged, uploadSessionId, handleUploadError, actions]);

  useEffect(() => {
    if (!useStaged || !uploadSessionId || !shouldReconcileSession.current) {
      return undefined;
    }

    let cancelled = false;

    const reconcileSession = async () => {
      try {
        const { documents } = await listSessionDocuments(uploadSessionId);
        if (cancelled) {
          return;
        }

        const grouped = { lease: [], ledger: [], other: [] };
        for (const doc of documents || []) {
          if (grouped[doc.category]) {
            grouped[doc.category].push(toDisplayFile(doc));
          }
        }

        setLeaseFile(grouped.lease);
        setLedgerFile(grouped.ledger);
        setOtherFiles(grouped.other);
      } catch (error) {
        if (!cancelled) {
          handleUploadError(
            getApiErrorMessage(
              error,
              'Unable to load your staged documents. Please re-upload your files.',
            ),
          );
        }
      }
    };

    reconcileSession();

    return () => {
      cancelled = true;
    };
  }, [useStaged, uploadSessionId, handleUploadError]);

  const hasRequiredFiles =
    leaseFile.length > 0 &&
    ledgerFile.length > 0 &&
    (acceptableDocument !== '' ? otherFiles.length > 0 : true);

  const documentsSize = getClaimDocumentsSize({ leaseFile, ledgerFile, otherFiles });
  const documentsAreTooLarge =
    !useStaged && exceedsClaimDocumentsSizeLimit({ leaseFile, ledgerFile, otherFiles });

  const handleNextButton = () => {
    if (documentsAreTooLarge) {
      handleUploadError(CLAIM_DOCUMENTS_TOO_LARGE_MESSAGE);
      return;
    }

    actions.setDataAction({
      ...formData.data,
      leaseFile,
      ledgerFile,
      otherFiles,
      uploadSessionId: useStaged ? uploadSessionId : formData.data.uploadSessionId,
    });
    handleNext();
  };

  const renderUpload = (category, files, setFiles, uploadProps) => {
    if (useStaged) {
      return (
        <StagedDocumentUploadField
          category={category}
          uploadSessionId={uploadSessionId}
          onError={handleUploadError}
          onFilesChange={setFiles}
          onUploadingChange={trackUploading}
          initialFiles={files}
          {...uploadProps}
        />
      );
    }

    return (
      <Box sx={documentUploadFileListSx}>
        <DocumentUpload initialFiles={files} onChange={setFiles} {...uploadProps} />
      </Box>
    );
  };

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="20px">
        {renderUpload('lease', leaseFile, setLeaseFile, {
          title: (
            <>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 500,
                  color: colors.neutral[900],
                  marginTop: '8px',
                }}
              >
                Upload your Lease Agreement (Required)
              </Typography>

              <Typography variant="small" style={{ color: colors.purple[600] }}>
                Please be sure to upload the countersigned lease agreement that <br /> matches the
                previously entered lease dates
              </Typography>
            </>
          ),
          description: documentUploadDescription,
          maxFiles: 10,
          accept: fileTypes,
        })}

        {renderUpload('ledger', ledgerFile, setLedgerFile, {
          title: (
            <>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 500,
                  color: colors.neutral[900],
                  marginTop: '8px',
                }}
              >
                Upload your Detailed Resident Ledger(Required)
              </Typography>

              <Typography variant="small" style={{ color: colors.purple[600] }}>
                Please be sure to upload the resident's ledger that is for the full-term <br />
                lease term and includes all charges, credits, and payments
              </Typography>
            </>
          ),
          description: documentUploadDescription,
          maxFiles: 10,
          accept: fileTypes,
        })}

        {renderUpload('other', otherFiles, setOtherFiles, {
          title: (
            <>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 500,
                  color: colors.neutral[900],
                  marginTop: '8px',
                }}
              >
                Upload your Other Documents ({acceptableDocument !== '' ? 'Required' : 'Optional'})
              </Typography>
              <Typography variant="small" style={{ color: colors.purple[600] }}>
                {formData.data.damageType?.tenantDefault || formData.data.damageType?.damageOnly ? (
                  <>
                    Other Documents means verifiable legal or eviction filing invoices, photos of
                    damages, pre and post move-in inspection report, and/or any other documents that
                    are relevant to this claim.
                  </>
                ) : (
                  <>
                    Other Documents means legal or eviction filing invoices, photos of damages,
                    initial and final inspection reports, 90-day intent to move in documents, and/or
                    any other documents that are relevant to this claim
                  </>
                )}
              </Typography>
            </>
          ),
          description: documentUploadDescription,
          maxFiles: 10,
          accept: fileTypes,
        })}

        {acceptableDocument !== '' && (
          <Alert
            showIcon
            showClose={false}
            showBorder={false}
            type="info"
            variant="contained"
            description={`Please upload any document or documentation that confirms the resident's intent to move in the unit within 90 days of the lease start date under Other Documents`}
          />
        )}

        {documentsAreTooLarge && (
          <Alert
            showIcon
            showClose={false}
            showBorder={false}
            type="error"
            variant="contained"
            title="Documents too large"
            description={`The combined size of all documents is ${formatClaimDocumentsSizeMb(
              documentsSize,
            )} MB. ${CLAIM_DOCUMENTS_TOO_LARGE_MESSAGE}`}
          />
        )}
      </Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mt: 4, gap: '16px' }}
      >
        <Button size="medium" variant="secondary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          variant="primary"
          onClick={handleNextButton}
          disabled={
            !hasRequiredFiles ||
            documentsAreTooLarge ||
            (useStaged && (!uploadSessionId || activeUploads > 0))
          }
        >
          Next
        </Button>
      </Box>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    formData: state.getIn(['formData']),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setDataAction,
        setErrorAction,
      },
      dispatch,
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form5);
